import React, { useState, useEffect, useRef } from 'react';
import Modal from '../common/Modal';
import { TeamService } from '../../services/team.service';
import { toast } from 'react-hot-toast';
import Button from '../common/Button';
import { UserRoles } from '../../constants/userRoles';
import { Role } from '../../types/team.types';
import { RolesService } from '../../services/roles.service';
import { useAppSelector } from '../../hooks/useRedux';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  teamId?: string;
  entityType?: string;
  roles?: Role[];
}

interface User {
  id: string;
  email: string;
  role_id: number;
}

interface SelectedUser {
  id: string;
  role_id: number;
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  teamId,
  entityType = 'Team',
  roles = []
}) => {
  const { team_id } = useAppSelector(state => state.auth);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchText, setSearchText] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Map<string, SelectedUser>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [error, setError] = useState('');
  const modalContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      fetchAvailableUsers();
    } else {
      // Reset state when modal closes
      setSelectedUsers(new Map());
      setSearchText('');
      setError('');
    }
  }, [isOpen]);

  const fetchAvailableUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const { users } = await TeamService.getUsersWithoutTeam(team_id || undefined, teamId || undefined);
      setAvailableUsers(users);
      setFilteredUsers(users);
    } catch (error) {
      console.error('Error fetching available users:', error);
      toast.error('Failed to load available users');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    
    if (value === '') {
      setFilteredUsers(availableUsers);
    } else {
      const filtered = availableUsers.filter(user => 
        user.email.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  };

  const toggleUserSelection = (user: User) => {
    const newSelected = new Map(selectedUsers);
    if (newSelected.has(user.id)) {
      newSelected.delete(user.id);
    } else {
      newSelected.set(user.id, { id: user.id, role_id: user.role_id });
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Map());
    } else {
      const newSelected = new Map();
      filteredUsers.forEach(user => {
        newSelected.set(user.id, { id: user.id, role_id: user.role_id });
      });
      setSelectedUsers(newSelected);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (selectedUsers.size === 0) {
      setError('Please select at least one user');
      return;
    }

    try {
      setIsLoading(true);
      const selectedUsersArray = Array.from(selectedUsers.values());
      
      await TeamService.addBulkMembersToTeam(
        selectedUsersArray.map(user => ({
          user_id: user.id,
          role_id: user.role_id
        })), 
        teamId || ''
      );
      
      toast.success(`${selectedUsers.size} member${selectedUsers.size > 1 ? 's' : ''} added successfully`);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error adding members:', error);
      setError(error.message || 'Failed to add members');
      toast.error('Failed to add members');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Add User${selectedUsers.size > 0 ? `s (${selectedUsers.size} selected)` : ''} to Team`}
    >
      <div 
        ref={modalContentRef}
        className="max-h-[calc(100vh-200px)] flex flex-col"
      >
        {isLoadingUsers ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : availableUsers.length === 0 ? (
          <div className="py-4 text-center">
            <p className="text-gray-500">No available users found to add to this {entityType.toLowerCase()}.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col h-full space-y-4">
            {/* Search Bar - Fixed at top */}
            <div className="sticky top-0 bg-white z-10 pb-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchText}
                  onChange={handleSearchChange}
                  placeholder="Search users by email..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Users List - Scrollable */}
            <div className="flex-1 min-h-0">
              <div className="border border-gray-200 rounded-md overflow-hidden">
                {/* Select All Header - Sticky */}
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 sticky top-0 z-10">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Select All ({filteredUsers.length} users)
                    </span>
                  </label>
                </div>

                {/* Users List - Scrollable */}
                <div className="overflow-y-auto max-h-[calc(100vh-400px)]">
                  {filteredUsers.map(user => (
                    <label
                      key={user.id}
                      className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-200 last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={selectedUsers.has(user.id)}
                        onChange={() => toggleUserSelection(user)}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-3 block text-sm text-gray-700">{user.email}</span>
                      {/* <span className="ml-auto text-xs text-gray-500">
                        Role ID: {user.role_id}
                      </span> */}
                    </label>
                  ))}

                  {filteredUsers.length === 0 && (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center bg-gray-50">
                      No users found matching your search
                    </div>
                  )}
                </div>
              </div>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}
            
            {/* Footer - Fixed at bottom */}
            <div className="sticky bottom-0 bg-white pt-4 border-t border-gray-200 mt-auto">
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={isLoading}
                  disabled={isLoading || selectedUsers.size === 0}
                >
                  Add {selectedUsers.size > 0 ? `(${selectedUsers.size})` : ''} Member{selectedUsers.size !== 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default AddMemberModal;